/**
 * Marketing-cookie consent, shared across every *.mucka.ai subdomain.
 *
 * The answer lives in ONE cookie on the parent domain, `mucka_marketing_consent`
 * = `granted` | `denied`, so mucka.ai and app.mucka.ai read the same thing. The
 * website's banner keeps its own localStorage copy too, but localStorage never
 * crosses subdomains, which is how the app spent its first month unable to see
 * a single yes (Meta Pixel dark for every visitor).
 *
 * Asked on the sign-up page only (SignupCookieConsent). Paid Meta clicks land
 * straight on app.mucka.ai/signup, so for most of them that page IS the website;
 * the logged-in app never asks and never runs an ad pixel.
 *
 * Deliberately directive-free (anti-pattern #18): the banner, the Pixel and the
 * tests all import it.
 */

export const MARKETING_CONSENT_COOKIE = "mucka_marketing_consent";
export const MARKETING_CONSENT_EVENT = "mucka:marketing-consent";

export type MarketingConsent = "granted" | "denied";

/** A year, matching the website banner. The ICO suggests re-asking periodically. */
const MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/**
 * The cookie domain for a hostname: the parent `.mucka.ai` on any Mucka host, so
 * every subdomain shares one answer; host-only everywhere else (localhost, a
 * Vercel preview), where a `.mucka.ai` domain would be refused by the browser.
 */
export function consentCookieDomain(hostname: string): string | null {
  const h = hostname.toLowerCase();
  return h === "mucka.ai" || h.endsWith(".mucka.ai") ? ".mucka.ai" : null;
}

/** The Set-Cookie string for an answer. Pure, so the attributes are testable. */
export function buildConsentCookie(value: MarketingConsent, hostname: string, protocol: string): string {
  const parts = [`${MARKETING_CONSENT_COOKIE}=${value}`, "path=/", `max-age=${MAX_AGE_SECONDS}`, "samesite=lax"];
  const domain = consentCookieDomain(hostname);
  if (domain) parts.push(`domain=${domain}`);
  if (protocol === "https:") parts.push("secure");
  return parts.join("; ");
}

/** The visitor's answer, or null when they have not been asked (or the value is junk). */
export function readMarketingConsent(): MarketingConsent | null {
  if (typeof document === "undefined") return null;
  const prefix = `${MARKETING_CONSENT_COOKIE}=`;
  const raw = document.cookie.split("; ").find((c) => c.startsWith(prefix))?.slice(prefix.length);
  return raw === "granted" || raw === "denied" ? raw : null;
}

export function hasMarketingConsent(): boolean {
  return readMarketingConsent() === "granted";
}

/** Record an answer and tell anything listening (the Pixel loads on a yes, at once). */
export function setMarketingConsent(value: MarketingConsent): void {
  if (typeof document === "undefined") return;
  document.cookie = buildConsentCookie(value, window.location.hostname, window.location.protocol);
  window.dispatchEvent(new CustomEvent<MarketingConsent>(MARKETING_CONSENT_EVENT, { detail: value }));
}

export function onMarketingConsentChange(cb: (value: MarketingConsent) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent<MarketingConsent>).detail);
  window.addEventListener(MARKETING_CONSENT_EVENT, handler);
  return () => window.removeEventListener(MARKETING_CONSENT_EVENT, handler);
}
